import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { message, Modal, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { GripVertical, Play } from "lucide-react";
import {
  DELETE_COMPANY_PRODUCT,
  GET_COMPANY_PRODUCTS,
  Get_Expert_Video,
  REORDER_COMPANY_PRODUCTS,
} from "../../../../services/userAPI";
import TrainingModuleEditIcon from "../../../../Images/trainingModuleEditIcon.png";
import TrainingModuleDeleteIcon from "../../../../Images/trainingModuleDeleteIcon.png";
import TrainingModuleTimingIcon from "../../../../Images/trainingModuleTimingIcon.png";
import TrainingModuleLanguageIcon from "../../../../Images/trainingModuleLanguageIcon.png";

const pad2 = (n) => String(n ?? 0).padStart(2, "0");

const BusinessUnderstanding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ROLE_ID = location.state?.roleId ? parseInt(location.state.roleId) : null;
  const ACCOUNT_ID = location.state?.accountId ? parseInt(location.state.accountId) : null;

  const { data: productsData, loading, refetch } = useQuery(GET_COMPANY_PRODUCTS, {
    variables: {
      accountId: ACCOUNT_ID,
      jobroleId: ROLE_ID,
      pagingCriteria: {
        limit: 20,
        cursor: null,
        sortByField: "created_at",
        sortOrder: "desc",
      },
      productStatus: null,
    },
    skip: !ACCOUNT_ID,
    fetchPolicy: "network-only",
  });

  const { data: languagesData } = useQuery(Get_Expert_Video);
  const languages = languagesData?.mdLanguages || [];

  const [deleteProduct] = useMutation(DELETE_COMPANY_PRODUCT);
  const [reorderProducts] = useMutation(REORDER_COMPANY_PRODUCTS);

  const [videos, setVideos] = useState([]);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const dragIndex = useRef(null);

  // Map company products → video rows (only products that carry a video)
  useEffect(() => {
    const products = productsData?.getCompanyProducts?.data || [];
    setVideos(
      products
        .filter((p) => p.productVideo)
        .map((p) => {
          const v = p.productVideo;
          const code = v.code || "";
          return {
            id: p.id,
            title: v.title || p.title || p.productName || "",
            description: v.description || p.description || p.shortDescription || "",
            duration: `${pad2(v.videoLengthMins)}:${pad2(v.videoLengthSecs)} Min`,
            language:
              languages.find((l) => String(l.id) === String(v.languageId))?.name || "",
            languageId: v.languageId ?? null,
            thumbnail: code ? `https://img.youtube.com/vi/${code}/0.jpg` : "",
            videoCode: code,
            contentId: v.id,
          };
        })
    );
  }, [productsData, languagesData]);

  const goToAddVideo = (editing = null) =>
    navigate("/add-course-video", {
      state: {
        ...location.state,
        context: "business",
        editing,
      },
    });

  const handleDrop = (targetIndex) => {
    const fromIndex = dragIndex.current;
    if (fromIndex === null || fromIndex === targetIndex) return;
    setVideos((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(targetIndex, 0, moved);
      if (ACCOUNT_ID) {
        reorderProducts({
          variables: {
            accountId: ACCOUNT_ID,
            productIds: arr.map((v) => v.id),
            roleId: ROLE_ID,
          },
        }).catch((err) => message.error("Reorder failed: " + err.message));
      }
      return arr;
    });
  };

  const handleDelete = () => {
    const productId = deleteCandidate?.id;
    if (productId) {
      deleteProduct({ variables: { productId } })
        .then((res) => {
          if (res?.data?.deleteProducts?.success === false) {
            throw new Error(res.data.deleteProducts.message || "Delete failed");
          }
          message.success("Video deleted successfully!");
          refetch();
        })
        .catch((err) => message.error("Delete failed: " + err.message));
    }
    setDeleteCandidate(null);
  };

  return (
    <div className="rounded-[16px] bg-white p-5">
      <h1 className="mb-5 font-['Inter'] text-[20px] font-semibold leading-[100%] tracking-[-0.2px] text-black">
        Business Understanding
      </h1>

      {/* Stat card + Add button */}
      <div className="mb-5 flex items-start justify-between">
        <div className="flex items-center gap-[18px] rounded-[12px] border border-[#9747FF] bg-[#F3EAFF] px-4 py-3">
          <span className="font-['Inter'] text-[14px] font-semibold text-[#9747FF]">
            Total Videos
          </span>
          <span className="font-['Inter'] text-[14px] font-semibold text-[#9747FF]">
            {videos.length}
          </span>
        </div>
        <button
          type="button"
          onClick={() => goToAddVideo()}
          className="flex items-center justify-center rounded-[8px] bg-[#AD0101] px-6 py-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#8C0101]"
        >
          Add Video
        </button>
      </div>

      <div className="border-t border-[#E8E8E8]" />

      {videos.length > 0 && (
        <div className="mt-5 flex justify-center">
          <span className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] px-[10px] py-1 font-['Inter'] text-[12px] text-[#888888]">
            <GripVertical size={12} /> Drag to reorder
          </span>
        </div>
      )}

      {/* Video rows */}
      <div className="mt-5 flex flex-col gap-6">
        {loading ? (
          <div className="flex min-h-[100px] items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#AD0101] border-t-transparent" />
          </div>
        ) : videos.length === 0 ? (
          <p className="py-16 text-center font-['Inter'] text-[14px] text-[#A0A0A0]">
            No Data Available
          </p>
        ) : (
          videos.map((video, index) => (
            <div
              key={video.id}
              draggable
              onDragStart={() => (dragIndex.current = index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(index)}
              className="flex items-center gap-[30px] rounded-[15px] border border-[#CBC8C8] bg-white px-2 py-3"
            >
              <GripVertical
                size={18}
                className="ml-1 shrink-0 cursor-grab text-[#C9C9C9] active:cursor-grabbing"
              />
              <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#AD0101] font-['Inter'] text-[13px] font-semibold text-white">
                {index + 1}
              </span>
              {video.thumbnail ? (
                <div className="relative h-[88px] w-[96px] shrink-0">
                  <img
                    src={video.thumbnail}
                    alt=""
                    className="h-[88px] w-[96px] rounded-[10px] object-cover"
                  />
                  <Play
                    size={22}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white"
                    fill="white"
                  />
                </div>
              ) : (
                <div className="flex h-[88px] w-[96px] shrink-0 items-center justify-center rounded-[10px] bg-[#E8B4B4]">
                  <Play size={26} className="text-[#AD0101]" fill="#AD0101" />
                </div>
              )}

              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <h3 className="font-['Inter'] text-[15px] font-semibold text-[#101828]">
                  {video.title}
                </h3>
                <Tooltip title={video.description} placement="topLeft">
                  <p className="max-w-[420px] cursor-default truncate font-['Inter'] text-[13px] text-[#9CA3AF]">
                    {video.description}
                  </p>
                </Tooltip>
                <div className="mt-0.5 flex items-center gap-3">
                  <span className="flex items-center gap-1 font-['Inter'] text-[13px] leading-[18px] text-[#888888]">
                    <img
                      src={TrainingModuleTimingIcon}
                      alt=""
                      className="h-3.5 w-3.5 object-contain"
                    />
                    {video.duration}
                  </span>
                  {video.language && (
                    <span className="flex items-center gap-1 font-['Inter'] text-[13px] leading-[18px] text-[#888888]">
                      <img
                        src={TrainingModuleLanguageIcon}
                        alt=""
                        className="h-3.5 w-3.5 object-contain"
                      />
                      {video.language}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 pr-1">
                <button type="button" onClick={() => goToAddVideo(video)} title="Edit">
                  <img src={TrainingModuleEditIcon} alt="Edit" className="h-8 w-8 object-contain" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteCandidate(video)}
                  title="Delete"
                >
                  <img
                    src={TrainingModuleDeleteIcon}
                    alt="Delete"
                    className="h-8 w-8 object-contain"
                  />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        footer={null}
        centered
        width={460}
      >
        <div className="p-2">
          <h3 className="mb-2 text-lg font-semibold text-[#1F2020]">
            Are you sure you want to delete the Video?
          </h3>
          <p className="mb-6 text-sm text-[#888888]">
            Are you sure you want to delete "{deleteCandidate?.title}"? This action will
            permanently remove the video.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteCandidate(null)}
              className="rounded-lg border border-gray-300 px-6 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-lg bg-[#AD0101] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[#8C0101]"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessUnderstanding;
